import styled, { keyframes } from 'styled-components';

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

export const lineGrow = keyframes`
  from { width: 0; }
  to { width: 80px; }
`;

// ─── LAYOUT ───────────────────────────────────────────────
export const HomeWrap = styled.div`
  width: 100%;
  background: #ffffff;
`;

// ─── HERO ─────────────────────────────────────────────────
export const Hero = styled.section`
  min-height: 100vh;
  background: #03045e;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 120px 4rem 4rem;
  gap: 4rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 120px 2rem 4rem;
    text-align: center;
  }
`;

export const HeroLeft = styled.div`
  animation: ${fadeUp} 0.9s ease-out;
`;

export const HeroTag = styled.span`
  display: inline-block;
  background: #00b4d8;
  color: #03045e;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 3px;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 2px;
  margin-bottom: 1.8rem;
`;

export const HeroTitle = styled.h1`
  font-size: clamp(3rem, 5.5vw, 5.5rem);
  font-weight: 900;
  color: #ffffff;
  line-height: 1.05;
  letter-spacing: -3px;
  margin-bottom: 1.5rem;

  em {
    font-style: normal;
    color: #00b4d8;
  }
`;

export const HeroDesc = styled.p`
  font-size: 1.05rem;
  color: rgba(255,255,255,0.55);
  line-height: 1.9;
  max-width: 480px;
  margin-bottom: 2.5rem;

  @media (max-width: 900px) { margin: 0 auto 2.5rem; }
`;

export const HeroBtns = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 900px) { justify-content: center; }
`;

export const BtnSolid = styled.button`
  padding: 15px 36px;
  background: #00b4d8;
  color: #03045e;
  border: none;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.5px;

  &:hover { background: #90e0ef; transform: translateY(-2px); }
`;

export const BtnGhost = styled.button`
  padding: 15px 36px;
  background: transparent;
  color: rgba(255,255,255,0.7);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: 'Inter', sans-serif;

  &:hover {
    border-color: #00b4d8;
    color: #00b4d8;
  }
`;

export const HeroRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeUp} 1.1s ease-out;

  @media (max-width: 900px) { display: none; }
`;

export const HeroVisual = styled.div`
  width: 420px;
  height: 420px;
  border: 1px solid rgba(0,180,216,0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 340px; height: 340px;
    border: 1px solid rgba(0,180,216,0.12);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    width: 260px; height: 260px;
    border: 1px solid rgba(0,180,216,0.08);
    border-radius: 50%;
  }

  img {
    width: 200px;
    height: 200px;
    object-fit: contain;
    animation: ${float} 4s ease-in-out infinite;
    position: relative;
    z-index: 1;
    background: #ffffff;
    border-radius: 50%;
    padding: 2rem;
    box-shadow: 0 0 60px rgba(255,255,255,0.15);
  }
`;

// ─── STATS ────────────────────────────────────────────────
export const StatsBar = styled.section`
  background: #ffffff;
  border-top: 4px solid #00b4d8;
  border-bottom: 1px solid #f0f0f0;
`;

export const StatsInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 3rem 2rem;
  }
`;

export const StatItem = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 0; top: 20%;
    height: 60%;
    width: 1px;
    background: #f0f0f0;
  }

  &:last-child::after { display: none; }

  h3 {
    font-size: clamp(2.2rem, 3.5vw, 3rem);
    font-weight: 900;
    color: #03045e;
    letter-spacing: -2px;
    line-height: 1;
  }

  p {
    color: #aaa;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 0.6rem;
  }

  span {
    display: block;
    width: 24px;
    height: 3px;
    background: #00b4d8;
    margin: 0.8rem auto 0;
    border-radius: 2px;
  }
`;

// ─── SECTION BASE ─────────────────────────────────────────
export const Section = styled.section`
  padding: 7rem 4rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) { padding: 5rem 2rem; }
`;

export const Label = styled.span`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #00b4d8;
  margin-bottom: 1rem;
`;

export const SectionH2 = styled.h2`
  font-size: clamp(2rem, 3.5vw, 3rem);
  font-weight: 900;
  color: #03045e;
  letter-spacing: -2px;
  line-height: 1.1;
  margin-bottom: 1rem;
`;

export const SectionSub = styled.p`
  color: #888;
  font-size: 1rem;
  line-height: 1.8;
  max-width: 560px;
`;

// ─── QUOTE ────────────────────────────────────────────────
export const QuoteSection = styled.section`
  background: #03045e;
  padding: 8rem 4rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: 'H₂';
    position: absolute;
    right: -2rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 22rem;
    font-weight: 900;
    color: rgba(255,255,255,0.03);
    line-height: 1;
    pointer-events: none;
    letter-spacing: -10px;
  }

  @media (max-width: 768px) { padding: 5rem 2rem; }
`;

export const QuoteInner = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  text-align: center;

  .mark {
    display: inline-block;
    width: 48px;
    height: 3px;
    background: #00b4d8;
    margin-bottom: 3rem;
  }

  p {
    font-size: clamp(1rem, 1.8vw, 1.25rem);
    color: rgba(255,255,255,0.8);
    line-height: 2;
    font-style: italic;
    font-weight: 300;
    margin-bottom: 2.5rem;
  }

  cite {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-style: normal;
    color: #00b4d8;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 3px;
    text-transform: uppercase;

    &::before, &::after {
      content: '';
      display: block;
      width: 40px;
      height: 1px;
      background: #00b4d8;
      opacity: 0.5;
    }
  }
`;

// ─── PRODUCTS ─────────────────────────────────────────────
export const ProductsSection = styled.section`
  background: #f8faff;
  padding: 7rem 4rem;

  @media (max-width: 768px) { padding: 5rem 2rem; }
`;

export const ProductsInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 4rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const ProductCard = styled.div`
  background: #03045e;
  border-radius: 16px;
  padding: 2.5rem;
  transition: all 0.35s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0,180,216,0.15);

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 0; height: 3px;
    background: #00b4d8;
    transition: width 0.4s ease;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: #00b4d8;
    box-shadow: 0 20px 50px rgba(0,180,216,0.2);
    &::after { width: 100%; }
  }

  .num {
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 3px;
    color: #00b4d8;
    margin-bottom: 2rem;
    display: block;
    text-transform: uppercase;
  }

  img {
    width: 120px;
    height: 120px;
    object-fit: contain;
    margin: 0 auto 2.5rem;
    display: block;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 0.8rem;
    letter-spacing: -0.5px;
  }

  p {
    color: rgba(255,255,255,0.5);
    font-size: 0.88rem;
    line-height: 1.8;
    margin-bottom: 2rem;
  }

  .arrow {
    font-size: 1rem;
    color: #00b4d8;
    font-weight: 700;
  }
`;

// ─── BENEFITS ─────────────────────────────────────────────
export const BenefitSection = styled.section`
  background: #ffffff;
  padding: 7rem 4rem;

  @media (max-width: 768px) { padding: 5rem 2rem; }
`;

export const BenefitInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

export const BenefitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2.5rem;
`;

export const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  padding: 1.5rem;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #00b4d8;
    box-shadow: 0 4px 20px rgba(0,180,216,0.08);
  }

  .icon {
    width: 44px; height: 44px;
    background: #03045e;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    color: #00b4d8;
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  h4 {
    font-size: 0.95rem;
    font-weight: 700;
    color: #03045e;
    margin-bottom: 0.3rem;
  }

  p {
    font-size: 0.85rem;
    color: #888;
    line-height: 1.7;
  }
`;

export const BenefitVisual = styled.div`
  background: #03045e;
  border-radius: 16px;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const BenefitStat = styled.div`
  padding: 1.5rem;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;

  h3 {
    font-size: 2.5rem;
    font-weight: 900;
    color: #00b4d8;
    letter-spacing: -2px;
  }

  p {
    color: rgba(255,255,255,0.5);
    font-size: 0.82rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-top: 0.3rem;
  }
`;

// ─── VIDEO ────────────────────────────────────────────────
export const VideoSection = styled.section`
  background: #ffffff;
  padding: 8rem 4rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) { padding: 5rem 2rem; }
`;

export const VideoInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-top: 4rem;
  border: 2px solid #03045e;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const VideoItem = styled.div`
  position: relative;
  overflow: hidden;
  border-right: ${p => p.last ? 'none' : '2px solid #03045e'};

  &:hover .overlay { opacity: 1; }
  &:hover iframe { transform: scale(1.02); }
`;

export const VideoWrap = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    border: 0;
    transition: transform 0.5s ease;
  }
`;

export const VideoLabel = styled.div`
  padding: 1.5rem 2rem;
  background: #03045e;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  .info {
    h4 {
      color: #ffffff;
      font-size: 1rem;
      font-weight: 700;
      margin-bottom: 0.2rem;
    }
    p {
      color: rgba(255,255,255,0.35);
      font-size: 0.75rem;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      font-weight: 500;
    }
  }

  .num {
    font-size: 2.5rem;
    font-weight: 900;
    color: rgba(255,255,255,0.06);
    letter-spacing: -2px;
    line-height: 1;
    flex-shrink: 0;
  }
`;

// ─── GUARANTEE ────────────────────────────────────────────
export const GuaranteeSection = styled.section`
  background: #7f0000;
  padding: 6rem 4rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: #c0392b;
  }

  @media (max-width: 768px) { padding: 4rem 2rem; }
`;

export const GuaranteeInner = styled.div`
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  .badge {
    display: inline-block;
    border: 2px solid rgba(255,255,255,0.4);
    color: rgba(255,255,255,0.9);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 3px;
    text-transform: uppercase;
    padding: 6px 16px;
    border-radius: 2px;
    margin-bottom: 2rem;
  }

  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 900;
    color: white;
    letter-spacing: -2px;
    line-height: 1.1;
    margin-bottom: 1.5rem;
  }

  p {
    color: rgba(255,255,255,0.65);
    font-size: 1rem;
    line-height: 1.9;
    margin-bottom: 2.5rem;
  }
`;

export const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 36px;
  background: #ffffff;
  color: #7f0000;
  border-radius: 4px;
  font-weight: 800;
  font-size: 0.95rem;
  transition: all 0.25s ease;
  letter-spacing: 0.5px;

  &:hover {
    background: rgba(255,255,255,0.85);
    transform: translateY(-2px);
  }
`;
